import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, X, RotateCcw, PalmtreeIcon } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = ["approve", "reject", "cancel"];

export default function PtoRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [override, setOverride] = useState(null);
  const [action, setAction] = useState("approve");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const query = status === "all" ? "" : `?status=${status}`;
      const { data } = await api.get(`/tenant/pto/requests${query}`);
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const submitOverride = async () => {
    setBusy(true);
    try {
      await api.post(`/tenant/pto/requests/${override.id}/override`, { action, reason: reason || undefined });
      toast.success("Applied");
      setOverride(null); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pto-requests-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Time admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">PTO requests</h1>
          <p className="mt-2 text-muted-foreground">Workspace-wide oversight and admin override.</p>
        </div>
        <div className="min-w-[200px]">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="pto-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No PTO requests.</CardContent></Card>
      ) : (
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="p-3">Worker</th>
                <th className="p-3">Type</th>
                <th className="p-3">Period</th>
                <th className="p-3">Hours/day</th>
                <th className="p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`admin-pto-${r.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{r.worker_name}</div>
                    <div className="text-xs text-muted-foreground">{r.worker_email}</div>
                  </td>
                  <td className="p-3">
                    <div className="inline-flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: r.pto_type_color || "#8b95a7" }} />
                      {r.pto_type_label}
                    </div>
                    <div className="text-xs text-muted-foreground">{r.pto_type_code}</div>
                  </td>
                  <td className="p-3">{r.start_date}{r.start_date !== r.end_date && ` → ${r.end_date}`}</td>
                  <td className="p-3 tabular-nums">{r.hours_per_day}h</td>
                  <td className="p-3"><StatusPill status={r.status} /></td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" onClick={() => { setOverride(r); setAction("approve"); setReason(""); }} data-testid={`admin-pto-override-${r.id}`}>
                      <RotateCcw className="h-3 w-3" /> Override
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!override} onOpenChange={(v) => !v && setOverride(null)}>
        <DialogContent data-testid="pto-override-dialog">
          <DialogHeader><DialogTitle>Override PTO request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="pto-override-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pto-override-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverride(null)}>Cancel</Button>
            <Button onClick={submitOverride} disabled={busy} data-testid="pto-override-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: "bg-blue-500/15 text-blue-700",
    approved: "bg-success/15 text-success",
    rejected: "bg-destructive/15 text-destructive",
    canceled: "bg-muted text-muted-foreground",
  };
  return <Badge className={`rounded-full capitalize ${map[status] || map.canceled}`}>{status}</Badge>;
}
